import React, { useEffect, useRef, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonDatetime,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonPage,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { alertCircle, businessOutline, checkmarkCircle, locationOutline, personOutline, timeOutline } from 'ionicons/icons';
import moment from 'moment';
import { Api } from '../services/api';
import { CustomField, ShiftJob, ShiftOptions } from '../interfaces/rosterinterfaces.ds';
import './AddShiftComponent.css';

// "Add my shift": the worker adds their own shift in one step. Everything the
// shift needs is on this one screen; the button says what is still missing.
// It starts straight away (no waiting for approval); the supervisor checks it later.

interface AddShiftProps {
  api: Api;
  onDismiss: (result?: { action: string; timesheet_id: number; message?: string }) => void;
  onFullForm?: () => void;
}

type Need = 'job' | 'task' | 'supervisor' | 'start' | 'end' | 'break' | 'extra';

const BREAKS = [0, 15, 30, 45, 60];

const newRequestId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const niceTime = (hm: string) => (hm ? moment(hm, 'HH:mm').format('h:mm a') : '');

const minutesBetween = (start: string, end: string) => {
  const s = moment.duration(start).asMinutes();
  const e = moment.duration(end).asMinutes();
  return e - s < 0 ? e - s + 1440 : e - s;
};

const tagText = (j: ShiftJob) => {
  if (j.tag === 'recent' && j.last_worked) {
    const days = moment().startOf('day').diff(moment(j.last_worked), 'days');
    return days <= 0 ? 'You worked here today' : days === 1 ? 'You worked here yesterday' : `You worked here ${days} days ago`;
  }
  if (j.tag === 'today') return 'Crew working here today';
  if (j.tag === 'assigned') return 'You are on this job';
  if (j.kind === 'project') return 'Project';
  return '';
};

// Declared outside the screen so inputs inside keep their focus while typing.
const JobCard: React.FC<{ j: ShiftJob; selected?: boolean; onPick?: (j: ShiftJob) => void }> = ({ j, selected, onPick }) => (
  <button type="button" className={'shift-job' + (selected ? ' selected' : '')} onClick={() => onPick && onPick(j)}>
    <div className="shift-job-title">{j.title || j.project || 'Job'}</div>
    {j.client && <div className="shift-job-line"><IonIcon icon={businessOutline} /> {j.client}</div>}
    {j.site_address && <div className="shift-job-line"><IonIcon icon={locationOutline} /> {j.site_address}</div>}
    {tagText(j) && <div className="shift-job-tag">{tagText(j)}</div>}
  </button>
);

const Step: React.FC<{ n: number; title: string; done: boolean; id?: string; missing?: boolean; optional?: boolean; children?: React.ReactNode }> = ({ n, title, done, id, missing, optional, children }) => (
  <section className={'shift-step' + (missing ? ' missing' : '')} id={id}>
    <div className="shift-step-head">
      <span className={'shift-step-n' + (done ? ' done' : '')}>{done ? <IonIcon icon={checkmarkCircle} /> : n}</span>
      <span className="shift-step-title">{title}</span>
      {optional ? <span className="shift-tag optional">Optional</span> : done ? <span className="shift-tag done">Done</span> : <span className="shift-tag required">Required</span>}
    </div>
    {children}
  </section>
);

const AddShiftComponent: React.FC<AddShiftProps> = ({ api, onDismiss, onFullForm }) => {
  const [options, setOptions] = useState<ShiftOptions | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  // 1. Which job
  const [job, setJob] = useState<ShiftJob | null>(null);
  const [notListed, setNotListed] = useState(false);
  const [newSite, setNewSite] = useState('');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<ShiftJob[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [taskId, setTaskId] = useState<number | null>(null);
  const [costCodeId, setCostCodeId] = useState<number | null>(null);
  const [supervisorUserId, setSupervisorUserId] = useState<number | null>(null);

  // 2. When
  const [day, setDay] = useState<'today' | 'yesterday'>('today');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [breakMinutes, setBreakMinutes] = useState<number | null>(null);
  const [picker, setPicker] = useState<'start' | 'end' | null>(null);
  const [pickerValue, setPickerValue] = useState('07:00');

  // 3. Job details (the job's timesheet template)
  const [description, setDescription] = useState('');
  const [usage, setUsage] = useState('');
  const [pallets, setPallets] = useState('');
  const [stores, setStores] = useState('');
  const [trailerType, setTrailerType] = useState('');
  const [custom, setCustom] = useState<Record<string, string>>({});
  const [comment, setComment] = useState('');

  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ text: string; ok: boolean } | null>(null);
  const requestId = useRef(newRequestId());
  const contentRef = useRef<HTMLIonContentElement>(null);

  const isAdmin = !!api.authentication?.user && api.authentication.user.group_id <= 2;

  useEffect(() => {
    api.rosters.getShiftOptions().then((o) => {
      if (o) {
        setOptions(o);
      } else {
        setLoadFailed(true);
      }
    });
  }, []);

  // Search as they type (after a short pause)
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setResults(null);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      api.rosters.searchShiftJobs(q).then((r) => {
        setResults(r || []);
        setSearching(false);
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const pickJob = (picked: ShiftJob) => {
    setJob(picked);
    setNotListed(false);
    setTaskId(picked.task_id);
    setCostCodeId(null);
    setSupervisorUserId(null);
    setSearch('');
    contentRef.current?.scrollToTop(300);
  };

  const clearJob = () => {
    setJob(null);
    setNotListed(false);
    setNewSite('');
    setTaskId(null);
    setCostCodeId(null);
    setSupervisorUserId(null);
  };

  // What the button will do: clock on now, add it for the kiosk, or save the finished shift
  const jobClocking = job ? job.clocking : true;
  const action: 'start' | 'kiosk' | 'submit' = options?.clock_in_system && day === 'today' ? (jobClocking ? 'start' : 'kiosk') : 'submit';
  const blockedByOpenShift = !!options?.open_shift && action !== 'submit';

  const template = job?.kind === 'job' ? job.template : null;
  const showExtras = action === 'submit' && !!template && !template.docking_system_enabled;
  let customFields: CustomField[] = [];
  try {
    customFields = template?.custom_fields ? JSON.parse(template.custom_fields) : [];
  } catch (e) {
    customFields = [];
  }
  const trailerOptions = template?.trailer_types ? template.trailer_types.split(',').map((t) => t.trim()).filter(Boolean) : [];

  const hasJob = !!job || (notListed && newSite.trim().length >= 3);
  const needsSupervisor = !!options?.supervisor_required && (notListed || (!!job && !job.supervisor));
  const taskChoices = job?.kind === 'job' ? job.tasks : options?.tasks || [];
  const taskRequired = job?.kind === 'job' && job.tasks.length > 1;

  // What is still missing, in screen order
  const missing: { key: Need; label: string }[] = [];
  if (!hasJob) missing.push({ key: 'job', label: 'Job' });
  if (hasJob && taskRequired && !taskId) missing.push({ key: 'task', label: 'Work you are doing' });
  if (hasJob && needsSupervisor && !supervisorUserId) missing.push({ key: 'supervisor', label: 'Supervisor' });
  if (action === 'submit') {
    if (!start) missing.push({ key: 'start', label: 'Start time' });
    if (!end) missing.push({ key: 'end', label: 'Finish time' });
    if (breakMinutes === null) missing.push({ key: 'break', label: 'Break' });
    if (showExtras) {
      customFields.forEach((f) => {
        if (f.custom_required && !(custom[f.custom_name] || '').trim()) {
          missing.push({ key: 'extra', label: f.custom_name });
        }
      });
    }
  }
  const isMissing = (key: Need) => tried && missing.some((m) => m.key === key);

  const worked = start && end ? minutesBetween(start, end) - (breakMinutes || 0) : null;
  const overnight = start && end && moment.duration(end).asMinutes() < moment.duration(start).asMinutes();

  // Suggested times: their usual 7:00 start, finish 8.5 hours after the start
  const openPicker = (which: 'start' | 'end') => {
    const current = which === 'start' ? start : end;
    const suggested = which === 'start' ? '07:00' : moment(start || '07:00', 'HH:mm').add(8.5, 'hours').format('HH:mm');
    setPickerValue(current || suggested);
    setPicker(which);
  };

  const getLocation = (): Promise<{ lat: number; lng: number } | null> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });

  const scrollTo = (key: Need) => {
    document.getElementById(`shift-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const save = async () => {
    setTried(true);
    if (blockedByOpenShift) {
      return;
    }
    if (missing.length > 0) {
      setToast({ text: `Still needed: ${missing.map((m) => m.label).join(', ')}`, ok: false });
      scrollTo(missing[0].key);
      return;
    }
    setSaving(true);
    let location = null;
    if (action === 'start') {
      location = await getLocation();
      if (!location) {
        setSaving(false);
        setToast({ text: 'Turn on location for Taro Time to start your shift, then tap Start shift again.', ok: false });
        return;
      }
    }
    const data = {
      request_id: requestId.current,
      day,
      job_allocation_id: job?.kind === 'job' ? job.id : null,
      project_id: job?.kind === 'project' ? job.id : null,
      new_site: notListed ? newSite.trim() : null,
      job_task_id: taskId,
      project_cost_code_id: costCodeId,
      supervisor_user_id: supervisorUserId,
      start_time: action === 'submit' ? start : null,
      // "Start shift now" starts at this phone's time, like Clock out does, so both ends use the same clock
      client_time: action === 'start' ? moment().format('HH:mm:ss') : null,
      end_time: action === 'submit' ? end : null,
      break_minutes: breakMinutes || 0,
      comment: comment.trim() || null,
      description: description.trim() || null,
      usage: usage || null,
      pallets: pallets || null,
      stores: stores.split(',').map((s) => s.trim()).filter(Boolean),
      trailer_type: trailerType || null,
      custom_data: custom,
      location,
    };
    const result = await api.rosters.addMyShift(data);
    setSaving(false);
    if (result.status === 200 && result.timesheet_id) {
      onDismiss({ action: result.action || action, timesheet_id: result.timesheet_id, message: result.message });
      return;
    }
    setToast({ text: result.message || 'Your shift could not be saved. Please try again.', ok: false });
    if (result.field && ['job', 'task', 'supervisor', 'start', 'end', 'break', 'extra'].includes(result.field)) {
      scrollTo(result.field as Need);
    }
  };

  let stepNo = 0;
  const dateFor = (d: 'today' | 'yesterday') => (d === 'today' ? options?.today : options?.yesterday) || moment().format('YYYY-MM-DD');

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton color="medium" onClick={() => onDismiss()}>Cancel</IonButton>
          </IonButtons>
          <IonTitle>Add my shift</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={contentRef} className="shift-content">
        {!options && !loadFailed && (
          <div className="shift-loading"><IonSpinner /> Loading your jobs...</div>
        )}
        {loadFailed && (
          <div className="shift-banner danger">
            Could not load your jobs. Check your signal, then close this and try again.
          </div>
        )}
        {options && (
          <div className="shift-form">
            {/* 1. Which job */}
            <Step n={++stepNo} title="Which job are you on?" done={hasJob} id="shift-job" missing={isMissing('job')}>
              {job ? (
                <div className="shift-picked">
                  <JobCard j={job} selected />
                  <IonButton fill="clear" size="small" onClick={clearJob}>Change job</IonButton>
                </div>
              ) : notListed ? (
                <div className="shift-notlisted">
                  <label className="shift-label" htmlFor="shift-newsite">Where are you working?</label>
                  <IonInput
                    id="shift-newsite"
                    className="shift-input"
                    placeholder="e.g. 22 Smith St driveway for Jones"
                    value={newSite}
                    onIonInput={(e) => setNewSite(String(e.detail.value || ''))}
                  />
                  <div className="shift-hint">Your supervisor will link it to the right job later.</div>
                  <IonButton fill="clear" size="small" onClick={clearJob}>Back to the job list</IonButton>
                </div>
              ) : (
                <>
                  <IonSearchbar
                    className="shift-search"
                    placeholder="Search job, client or address"
                    value={search}
                    debounce={0}
                    onIonInput={(e) => setSearch(String(e.detail.value || ''))}
                  />
                  {searching && <div className="shift-hint"><IonSpinner name="dots" /> Searching...</div>}
                  {results !== null && !searching && results.length === 0 && (
                    <div className="shift-hint">Nothing found for "{search.trim()}".</div>
                  )}
                  {(results ?? options.jobs).map((j) => (
                    <JobCard key={`${j.kind}-${j.id}`} j={j} onPick={pickJob} />
                  ))}
                  {results === null && options.jobs.length === 0 && (
                    <div className="shift-hint">Search above to find your job.</div>
                  )}
                  <button type="button" className="shift-notlisted-btn" onClick={() => { setNotListed(true); setSearch(''); }}>
                    My job is not in the list
                  </button>
                </>
              )}

              {/* Work, section and supervisor belong to the job */}
              {hasJob && taskChoices.length > 0 && (taskRequired || job?.kind !== 'job') && (
                <div className={'shift-sub' + (isMissing('task') ? ' missing' : '')} id="shift-task">
                  <div className="shift-label">What work are you doing?{!taskRequired && <span className="shift-soft"> (optional)</span>}</div>
                  {taskChoices.length <= 6 ? (
                    <div className="shift-chips">
                      {taskChoices.map((t) => (
                        <button type="button" key={t.id} className={'shift-chip' + (taskId === t.id ? ' on' : '')} onClick={() => setTaskId(taskId === t.id && !taskRequired ? null : t.id)}>
                          {t.title}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <IonSelect className="shift-select" interface="action-sheet" placeholder="Pick the work" value={taskId} onIonChange={(e) => setTaskId(e.detail.value || null)}>
                      {taskChoices.map((t) => (
                        <IonSelectOption key={t.id} value={t.id}>{t.title}</IonSelectOption>
                      ))}
                    </IonSelect>
                  )}
                </div>
              )}
              {job && job.cost_codes.length > 0 && (
                <div className="shift-sub">
                  <div className="shift-label">Which part of the job?<span className="shift-soft"> (optional)</span></div>
                  <IonSelect className="shift-select" interface="action-sheet" placeholder="Pick a section" value={costCodeId} onIonChange={(e) => setCostCodeId(e.detail.value || null)}>
                    {job.cost_codes.map((c) => (
                      <IonSelectOption key={c.id} value={c.id}>{c.label}</IonSelectOption>
                    ))}
                  </IonSelect>
                </div>
              )}
              {job?.supervisor && (
                <div className="shift-line"><IonIcon icon={personOutline} /> Supervisor: <strong>{job.supervisor}</strong></div>
              )}
              {hasJob && !job?.supervisor && options.supervisors.length > 0 && (
                <div className={'shift-sub' + (isMissing('supervisor') ? ' missing' : '')} id="shift-supervisor">
                  <div className="shift-label">Who is your supervisor?{!needsSupervisor && <span className="shift-soft"> (optional)</span>}</div>
                  <IonSelect className="shift-select" interface="action-sheet" placeholder="Pick your supervisor" value={supervisorUserId} onIonChange={(e) => setSupervisorUserId(e.detail.value || null)}>
                    {options.supervisors.map((s) => (
                      <IonSelectOption key={s.user_id} value={s.user_id}>{s.name}</IonSelectOption>
                    ))}
                  </IonSelect>
                </div>
              )}
            </Step>

            {/* 2. Which day */}
            <Step n={++stepNo} title="Which day?" done={true}>
              <div className="shift-days">
                {(['today', 'yesterday'] as const).map((d) => (
                  <button type="button" key={d} className={'shift-day' + (day === d ? ' on' : '')} onClick={() => setDay(d)}>
                    <span className="shift-day-name">{d === 'today' ? 'Today' : 'Yesterday'}</span>
                    <span className="shift-day-date">{moment(dateFor(d)).format('ddd D MMM')}</span>
                  </button>
                ))}
              </div>
            </Step>

            {/* 3. Times, or clock on now */}
            {action === 'start' && (
              blockedByOpenShift ? (
                <div className="shift-banner danger">
                  You are still clocked on from {niceTime(options.open_shift?.start_time || '')}. Clock off that shift first.
                  <IonButton expand="block" color="danger" className="mt-2" onClick={() => onDismiss({ action: 'open', timesheet_id: options.open_shift!.timesheet_id })}>
                    Open that shift
                  </IonButton>
                </div>
              ) : (
                <div className="shift-banner info">
                  <IonIcon icon={timeOutline} /> You will be clocked on now ({moment().format('h:mm a')}). Tap <strong>Clock out</strong> when you finish.
                </div>
              )
            )}
            {action === 'kiosk' && (
              <div className="shift-banner info">
                <IonIcon icon={timeOutline} /> This job uses the kiosk. Your shift will be added, then clock on at the kiosk.
              </div>
            )}
            {action === 'submit' && (
              <Step n={++stepNo} title="Your hours" done={!!start && !!end && breakMinutes !== null}>
                <div className="shift-times">
                  <button type="button" id="shift-start" className={'shift-time' + (isMissing('start') ? ' missing' : '') + (start ? ' set' : '')} onClick={() => openPicker('start')}>
                    <span className="shift-time-label">Start</span>
                    <span className="shift-time-value">{start ? niceTime(start) : 'Tap to set'}</span>
                  </button>
                  <button type="button" id="shift-end" className={'shift-time' + (isMissing('end') ? ' missing' : '') + (end ? ' set' : '')} onClick={() => openPicker('end')}>
                    <span className="shift-time-label">Finish</span>
                    <span className="shift-time-value">{end ? niceTime(end) : 'Tap to set'}</span>
                  </button>
                </div>
                <div className={'shift-sub' + (isMissing('break') ? ' missing' : '')} id="shift-break">
                  <div className="shift-label">Break</div>
                  <div className="shift-chips">
                    {BREAKS.map((b) => (
                      <button type="button" key={b} className={'shift-chip' + (breakMinutes === b ? ' on' : '')} onClick={() => setBreakMinutes(b)}>
                        {b === 0 ? 'No break' : `${b} min`}
                      </button>
                    ))}
                  </div>
                </div>
                {worked !== null && worked > 0 && (
                  <div className="shift-total">
                    {(worked / 60).toFixed(2).replace(/\.?0+$/, '')} hours worked{overnight ? ' (finishes the next day)' : ''}
                  </div>
                )}
              </Step>
            )}

            {/* 4. The job's timesheet details */}
            {showExtras && template && (
              <Step
                n={++stepNo}
                title={template.title || 'Job details'}
                done={!missing.some((m) => m.key === 'extra')}
                id="shift-extra"
                missing={isMissing('extra')}
                optional={!customFields.some((f) => f.custom_required)}
              >
                {template.show_description && (
                  <div className="shift-sub">
                    <div className="shift-label">Description</div>
                    <IonInput className="shift-input" value={description} onIonInput={(e) => setDescription(String(e.detail.value || ''))} />
                  </div>
                )}
                {template.pay_unit && (
                  <div className="shift-sub">
                    <div className="shift-label">{template.pay_unit.charAt(0).toUpperCase() + template.pay_unit.slice(1)}</div>
                    <IonInput className="shift-input" type="number" inputmode="decimal" value={usage} onIonInput={(e) => setUsage(String(e.detail.value || ''))} />
                  </div>
                )}
                {template.show_pallet && (
                  <div className="shift-sub">
                    <div className="shift-label">Pallets</div>
                    <IonInput className="shift-input" type="number" inputmode="numeric" value={pallets} onIonInput={(e) => setPallets(String(e.detail.value || ''))} />
                  </div>
                )}
                {template.show_store && (
                  <div className="shift-sub">
                    <div className="shift-label">Stores <span className="shift-soft">(separate with commas)</span></div>
                    <IonInput className="shift-input" value={stores} onIonInput={(e) => setStores(String(e.detail.value || ''))} />
                  </div>
                )}
                {template.show_trailer_type && trailerOptions.length > 0 && (
                  <div className="shift-sub">
                    <div className="shift-label">Trailer type</div>
                    <IonSelect className="shift-select" interface="action-sheet" placeholder="Pick one" value={trailerType} onIonChange={(e) => setTrailerType(e.detail.value || '')}>
                      {trailerOptions.map((t) => (
                        <IonSelectOption key={t} value={t}>{t}</IonSelectOption>
                      ))}
                    </IonSelect>
                  </div>
                )}
                {customFields.map((f) => {
                  const empty = !(custom[f.custom_name] || '').trim();
                  return (
                    <div key={f.custom_name} className={'shift-sub' + (tried && f.custom_required && empty ? ' missing' : '')}>
                      <div className="shift-label">
                        {f.custom_name}
                        {f.custom_required ? <span className="shift-tag required small">Required</span> : <span className="shift-soft"> (optional)</span>}
                      </div>
                      {f.custom_help && <div className="shift-hint">{f.custom_help}</div>}
                      <IonInput
                        className="shift-input"
                        type={(['number', 'date', 'time', 'email', 'tel'].includes(f.custom_type) ? f.custom_type : 'text') as any}
                        value={custom[f.custom_name] || ''}
                        onIonInput={(e) => setCustom((prev) => ({ ...prev, [f.custom_name]: String(e.detail.value || '') }))}
                      />
                    </div>
                  );
                })}
              </Step>
            )}

            {action === 'submit' && (
              <Step n={++stepNo} title="Anything to add?" done={false} optional>
                <IonTextarea className="shift-input" autoGrow placeholder="e.g. rain stopped work for an hour" value={comment} onIonInput={(e) => setComment(String(e.detail.value || ''))} />
              </Step>
            )}

            <div className="shift-hint center">
              Your shift goes ahead straight away. Your supervisor will check it later.
            </div>
            {isAdmin && onFullForm && (
              <IonButton fill="clear" size="small" expand="block" onClick={onFullForm}>
                Add a shift for someone else (full form)
              </IonButton>
            )}
          </div>
        )}
      </IonContent>

      {options && (
        <IonFooter className="shift-footer">
          <div className={'shift-status' + (missing.length ? ' todo' : ' ready')}>
            {blockedByOpenShift ? (
              <><IonIcon icon={alertCircle} /> Clock off your other shift first</>
            ) : missing.length ? (
              <><IonIcon icon={alertCircle} /> Still needed: {missing.map((m) => m.label).join(', ')}</>
            ) : (
              <><IonIcon icon={checkmarkCircle} /> All done</>
            )}
          </div>
          <IonButton expand="block" size="large" className="shift-go" color={action === 'start' ? 'success' : 'primary'} disabled={saving || blockedByOpenShift} onClick={save}>
            {saving ? <IonSpinner name="crescent" /> : action === 'start' ? 'Start shift now' : action === 'kiosk' ? 'Add shift' : 'Save shift'}
          </IonButton>
        </IonFooter>
      )}

      {/* A sheet inside this screen, not a nested modal: buttons in a modal opened from a
          modal don't get taps in this app */}
      {picker && (
        <div className="shift-sheet-backdrop" onClick={() => setPicker(null)}>
          <div className="shift-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="shift-picker-title">{picker === 'start' ? 'Start time' : 'Finish time'}</div>
            {/* Our own Set button saves whatever the wheel shows, even the suggested time untouched */}
            <IonDatetime
              presentation="time"
              preferWheel
              minuteValues="0,15,30,45"
              value={`${dateFor(day)}T${pickerValue}:00`}
              onIonChange={(e) => {
                const value = String(e.detail.value || '');
                const hm = value.includes('T') ? value.split('T')[1].slice(0, 5) : value.slice(0, 5);
                if (/^\d{2}:\d{2}$/.test(hm)) setPickerValue(hm);
              }}
            />
            <div className="shift-picker-buttons">
              <IonButton fill="clear" color="medium" onClick={() => setPicker(null)}>Cancel</IonButton>
              <IonButton className="shift-picker-set" onClick={() => {
                if (picker === 'start') setStart(pickerValue); else setEnd(pickerValue);
                setPicker(null);
              }}>Set {niceTime(pickerValue)}</IonButton>
            </div>
          </div>
        </div>
      )}

      <IonToast
        isOpen={!!toast}
        message={toast?.text}
        duration={4000}
        position="top"
        color={toast?.ok ? 'success' : 'danger'}
        icon={toast?.ok ? checkmarkCircle : alertCircle}
        onDidDismiss={() => setToast(null)}
      />
    </IonPage>
  );
};

export default AddShiftComponent;
